<template>
  <div class="capture-root">
    <div class="topbar">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" @click="goBack" />
      <div class="topbar-actions">
        <button class="config-btn" @click="openConfig">Configuracion</button>
        <button class="refresh-btn" @click="fetchMonthlyData" :disabled="loading">
          {{ loading ? 'Actualizando...' : 'Actualizar' }}
        </button>
      </div>
    </div>

    <header class="header">
      <div>
        <h1>Incidencias de personal</h1>
      </div>
      <div class="header-actions">
        <label class="month-picker">
          Mes
          <input type="month" v-model="selectedMonth" @change="fetchMonthlyData" />
        </label>
        <label class="sort-picker">
          Ordenar por
          <select v-model="sortBy">
            <option value="total">Incidencias</option>
            <option value="employee_ident">Numero</option>
            <option value="employee_name">Nombre</option>
          </select>
        </label>
        <button class="sort-dir-btn" @click="toggleSortDirection">
          {{ sortDirection === 'asc' ? 'Ascendente' : 'Descendente' }}
        </button>
      </div>
    </header>

    <section class="kpi-strip">
      <article class="kpi-card kpi-card--primary">
        <span class="kpi-label">Incidencias del mes</span>
        <strong class="kpi-value">{{ totalIncidents }}</strong>
      </article>
      <article class="kpi-card kpi-card--accent">
        <span class="kpi-label">Empleados con incidencias</span>
        <strong class="kpi-value">{{ employeesWithIncidents }}</strong>
      </article>
      <article class="kpi-card kpi-card--accent">
        <span class="kpi-label">Mayor acumulado</span>
        <strong class="kpi-value">{{ topAccumulator }}</strong>
      </article>
    </section>

    <section v-if="error" class="error-box">{{ error }}</section>

    <section v-if="departmentTabs.length" class="department-tabs">
      <button
        v-for="tab in departmentTabs"
        :key="tab.key"
        class="department-tab-btn"
        :class="{ 'department-tab-btn--active': tab.key === activeDepartment }"
        @click="activeDepartment = tab.key"
      >
        {{ tab.label }} ({{ tab.count }})
      </button>
    </section>

    <section v-if="sortedActiveGroupRows.length" class="table-wrap">
      <table class="matrix-table">
        <thead>
          <tr>
            <th class="sticky-col">Empleado</th>
            <th
              v-for="day in days"
              :key="`h-${day}`"
              :class="['day-header', { 'week-start-header': isWeekStart(day) }]"
            >
              {{ day }}
            </th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in sortedActiveGroupRows" :key="row.employee_id">
            <td class="sticky-col sticky-name">
              <div class="name-wrap">
                <span class="name-text">{{ row.employee_ident || '---' }} - {{ row.employee_name }}</span>
              </div>
            </td>
            <td
              v-for="(value, idx) in row.daily"
              :key="`v-${row.employee_id}-${idx}`"
              :class="{ 'week-start-col': isWeekStart(idx + 1) }"
            >
              <button
                class="day-cell"
                :class="dayCellClass(Number(value || 0))"
                :style="dayCellStyle(Number(value || 0), idx + 1)"
                :disabled="Number(value || 0) <= 0"
                @click="openDayDetail(row, idx + 1)"
              >
                {{ Number(value || 0) }}
              </button>
            </td>
            <td class="total-cell">
              <span class="total-pill" :class="totalBadgeClass(Number(row.total || 0))">
                {{ Number(row.total || 0) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
    <section v-else-if="!loading" class="empty-box">
      No hay empleados visibles para este mes. Verifica que esten seleccionados en la configuracion.
    </section>

    <transition name="modal-fade">
      <div v-if="showConfigModal" class="modal-overlay" @click.self="closeConfig">
        <div class="modal-card">
          <div class="modal-head">
            <div>
              <h3>Configuracion de personal visible</h3>
              <p class="modal-subtitle">Selecciona los empleados que quieres mostrar en el tablero.</p>
            </div>
            <button class="small-btn" @click="closeConfig">Cerrar</button>
          </div>
          <div class="modal-actions">
            <button class="small-btn" @click="selectAll">Seleccionar todo</button>
            <button class="small-btn" @click="clearAll">Quitar todo</button>
          </div>
          <div class="options-list">
            <label v-for="row in rows" :key="`cfg-${row.employee_id}`" class="option">
              <input
                type="checkbox"
                :checked="selectedEmployees.includes(row.employee_id)"
                @change="toggleEmployee(row.employee_id, $event.target.checked)"
              />
              <span>{{ row.employee_ident || '---' }} - {{ row.employee_name }}</span>
            </label>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showDetailModal" class="modal-overlay" @click.self="closeDetailModal">
        <div class="modal-card notes-modal">
          <div class="modal-head">
            <div>
              <h3>Incidencias de {{ detailMeta.employeeName }}</h3>
              <p class="modal-subtitle">{{ detailMeta.date }} · {{ dayIncidents.length }} incidencia(s)</p>
            </div>
            <button class="small-btn" @click="closeDetailModal">Cerrar</button>
          </div>
          <div class="table-wrap">
            <table class="notes-table">
              <thead>
                <tr>
                  <th>Hora</th>
                  <th>Nivel</th>
                  <th>Tipo</th>
                  <th>Unidad</th>
                  <th>Costo</th>
                  <th>Firmado</th>
                  <th>Elabora</th>
                  <th>Descripcion</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="incident in dayIncidents" :key="incident.id">
                  <td>{{ formatHour(incident.occurrence_time) }}</td>
                  <td>
                    <span class="level-pill" :class="levelBadgeClass(incident.level)">
                      {{ levelLabel(incident.level) }}
                    </span>
                  </td>
                  <td>{{ incident.type || '—' }}</td>
                  <td>{{ incident.unit_short_id || 'Sin unidad' }}</td>
                  <td>{{ incident.cost ?? '—' }}</td>
                  <td>
                    <span class="signed-pill" :class="incident.acknowledgement_signature ? 'signed-pill--yes' : 'signed-pill--no'">
                      {{ incident.acknowledgement_signature ? 'Sí' : 'No' }}
                    </span>
                  </td>
                  <td>{{ incident.data_entry_clerk_ident }} - {{ incident.data_entry_clerk_name }}</td>
                  <td class="desc-cell">{{ incident.description || '—' }}</td>
                </tr>
                <tr v-if="!dayIncidents.length">
                  <td colspan="8" class="empty-cell">No hay incidencias para este día.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'RHIncidentsDashboard',
  data() {
    const now = new Date()
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return {
      permissions: [],
      selectedMonth: month,
      days: [],
      rows: [],
      selectedEmployees: [],
      loading: false,
      error: '',
      sortBy: 'total',
      sortDirection: 'desc',
      activeDepartment: '',
      showConfigModal: false,
      showDetailModal: false,
      dayIncidents: [],
      detailMeta: {
        employeeName: '',
        date: '',
      },
      levelOptions: [
        { value: '1', label: '1 - Leve' },
        { value: '2', label: '2 - Moderada' },
        { value: '3', label: '3 - Grave' },
        { value: '4', label: '4 - Muy Grave' },
      ],
      departmentOptions: [
        'Remolque A, B',
        'Remolque C',
        'Remolque D',
        'Administración',
        'Ventas',
        'Sistemas',
        'Operaciones',
        'Mantenimiento',
        'Almacén',
        'Montacargas',
      ],
      otherDepartmentKey: '__sin_departamento__',
    }
  },
  computed: {
    visibleRows() {
      const allowed = new Set(this.selectedEmployees || [])
      return (this.rows || []).filter((row) => allowed.has(row.employee_id))
    },
    departmentTabs() {
      const knownDepartments = new Set(this.departmentOptions)
      const counts = new Map()
      let otherCount = 0
      ;(this.visibleRows || []).forEach((row) => {
        const department = String(row?.employee_department || '').trim()
        if (knownDepartments.has(department)) {
          counts.set(department, (counts.get(department) || 0) + 1)
        } else {
          otherCount += 1
        }
      })
      const tabs = this.departmentOptions
        .filter((department) => counts.has(department))
        .map((department) => ({ key: department, label: department, count: counts.get(department) }))
      if (otherCount > 0) {
        tabs.push({ key: this.otherDepartmentKey, label: 'Sin departamento', count: otherCount })
      }
      return tabs
    },
    activeGroupRows() {
      const activeGroup = this.activeDepartment
      if (!activeGroup) return []
      const knownDepartments = new Set(this.departmentOptions)
      return (this.visibleRows || []).filter((row) => {
        const department = String(row?.employee_department || '').trim()
        if (activeGroup === this.otherDepartmentKey) {
          return !knownDepartments.has(department)
        }
        return department === activeGroup
      })
    },
    sortedActiveGroupRows() {
      const rows = [...(this.activeGroupRows || [])]
      const direction = this.sortDirection === 'desc' ? -1 : 1
      if (this.sortBy === 'total') {
        rows.sort((a, b) => (Number(a.total || 0) - Number(b.total || 0)) * direction)
        return rows
      }
      const sortKey = this.sortBy === 'employee_name' ? 'employee_name' : 'employee_ident'
      rows.sort((a, b) => {
        const aValue = String(a?.[sortKey] || '').trim()
        const bValue = String(b?.[sortKey] || '').trim()
        if (sortKey === 'employee_ident') {
          const aNum = Number(aValue)
          const bNum = Number(bValue)
          const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum)
          if (bothNumeric && aNum !== bNum) return (aNum - bNum) * direction
        }
        return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' }) * direction
      })
      return rows
    },
    totalIncidents() {
      return this.visibleRows.reduce((acc, row) => acc + Number(row.total || 0), 0)
    },
    employeesWithIncidents() {
      return this.visibleRows.filter((row) => Number(row.total || 0) > 0).length
    },
    topAccumulator() {
      const ranked = [...this.visibleRows]
        .filter((row) => Number(row.total || 0) > 0)
        .sort((a, b) => Number(b.total || 0) - Number(a.total || 0))
      return ranked.length ? `${ranked[0].employee_name} (${ranked[0].total})` : 'Sin datos'
    },
    maxDaily() {
      const values = (this.activeGroupRows || []).flatMap((row) =>
        (row.daily || []).map((value) => Number(value || 0))
      )
      return values.length ? Math.max(...values) : 0
    },
    maxTotal() {
      if (!this.visibleRows.length) return 0
      return Math.max(...this.visibleRows.map((row) => Number(row.total || 0)))
    },
  },
  watch: {
    departmentTabs: {
      immediate: true,
      handler(tabs) {
        if (!tabs.length) {
          this.activeDepartment = ''
          return
        }
        const isActiveValid = tabs.some((tab) => tab.key === this.activeDepartment)
        if (!isActiveValid) {
          this.activeDepartment = tabs[0].key
        }
      },
    },
  },
  created() {
    this.fetchCurrentUser()
  },
  methods: {
    hasPerm(perm) {
      if (!perm) return false
      if (this.permissions.includes(perm)) return true
      return this.permissions.includes(`access_control.${perm}`)
    },
    apiBase() {
      return process.env.API_DIRECTORY || ''
    },
    authHeaders() {
      return { Authorization: `Token ${localStorage.getItem('authToken')}` }
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: this.authHeaders(),
        })
        this.permissions = data.permissions || []
        if (!this.hasPerm('ui_access_rrhh_incidents_dashboard') && !this.hasPerm('ui_access_rrhh_module')) {
          this.$router.push({ name: 'MainPanel' })
          return
        }
        await this.fetchMonthlyData()
      } catch (error) {
        this.$router.push({ name: 'MainPanel' })
      }
    },
    parseSelectedMonth() {
      const [yearStr, monthStr] = (this.selectedMonth || '').split('-')
      return { year: Number(yearStr), month: Number(monthStr) }
    },
    async fetchMonthlyData() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get(`${this.apiBase()}/dashboard/rrhh-incidents-monthly-matrix`, {
          params: { month: this.selectedMonth },
          headers: this.authHeaders(),
        })
        this.days = Array.isArray(data?.days) ? data.days : []
        this.rows = Array.isArray(data?.series) ? data.series : []
        await this.fetchConfig()
      } catch (error) {
        this.error = 'No se pudo cargar el tablero de incidencias.'
      } finally {
        this.loading = false
      }
    },
    async fetchConfig() {
      try {
        const { data } = await axios.get(`${this.apiBase()}/dashboard/rrhh-incidents-config`, {
          headers: this.authHeaders(),
        })
        const fromApi = Array.isArray(data?.visible_employee_ids) ? data.visible_employee_ids : []
        this.selectedEmployees = data?.is_configured ? fromApi : (this.rows || []).map((row) => row.employee_id)
      } catch (error) {
        this.selectedEmployees = (this.rows || []).map((row) => row.employee_id)
      }
      this.syncEmployeeSelection()
    },
    syncEmployeeSelection() {
      const validIds = new Set((this.rows || []).map((row) => row.employee_id))
      this.selectedEmployees = (this.selectedEmployees || []).filter((id) => validIds.has(id))
    },
    openConfig() {
      this.showConfigModal = true
    },
    closeConfig() {
      this.showConfigModal = false
    },
    toggleEmployee(employeeId, checked) {
      if (checked) {
        if (!this.selectedEmployees.includes(employeeId)) {
          this.selectedEmployees = [...this.selectedEmployees, employeeId]
        }
      } else {
        this.selectedEmployees = this.selectedEmployees.filter((id) => id !== employeeId)
      }
      this.saveConfig()
    },
    selectAll() {
      this.selectedEmployees = (this.rows || []).map((row) => row.employee_id)
      this.saveConfig()
    },
    clearAll() {
      this.selectedEmployees = []
      this.saveConfig()
    },
    async saveConfig() {
      try {
        await axios.put(
          `${this.apiBase()}/dashboard/rrhh-incidents-config`,
          { visible_employee_ids: this.selectedEmployees || [] },
          { headers: this.authHeaders() }
        )
      } catch (error) {
        this.error = 'No se pudo guardar la configuracion de personal.'
      }
    },
    toggleSortDirection() {
      this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc'
    },
    async openDayDetail(row, day) {
      const { year, month } = this.parseSelectedMonth()
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      this.detailMeta = { employeeName: row.employee_name, date: dateStr }
      this.dayIncidents = []
      this.showDetailModal = true
      try {
        const { data } = await axios.get(`${this.apiBase()}/dashboard/rrhh-incidents-employee-day`, {
          params: { employee_id: row.employee_id, date: dateStr },
          headers: this.authHeaders(),
        })
        this.dayIncidents = Array.isArray(data?.results) ? data.results : []
      } catch (error) {
        this.dayIncidents = []
      }
    },
    closeDetailModal() {
      this.showDetailModal = false
      this.dayIncidents = []
    },
    isWeekStart(day) {
      const n = Number(day || 0)
      return n > 0 && ((n - 1) % 7 === 0)
    },
    dayCellClass(value) {
      if (value <= 0) return ['is-empty']
      return ['is-clickable']
    },
    dayCellStyle(value, day) {
      const max = this.maxDaily || 1
      const isWeekStart = this.isWeekStart(day)
      if (!value) {
        return isWeekStart ? { border: '1px solid rgba(220, 38, 38, 0.28)' } : {}
      }
      const intensity = Math.min(0.95, value / max)
      const lightness = Math.round(94 - intensity * 42)
      return {
        background: `hsl(0deg 78% ${lightness}%)`,
        color: intensity > 0.5 ? '#fff5f5' : '#7f1d1d',
        boxShadow: `inset 0 0 0 1px rgba(127, 29, 29, ${0.12 + intensity * 0.3})`,
        border: isWeekStart ? '1px solid rgba(220, 38, 38, 0.45)' : '1px solid transparent',
      }
    },
    totalBadgeClass(total) {
      if (!total) return 'neutral'
      if (!this.maxTotal) return 'soft'
      const ratio = total / this.maxTotal
      if (ratio >= 0.85) return 'strong'
      if (ratio >= 0.55) return 'medium'
      return 'soft'
    },
    levelLabel(levelValue) {
      const option = this.levelOptions.find((item) => String(item.value) === String(levelValue || ''))
      return option?.label || levelValue || 'N/A'
    },
    levelBadgeClass(levelValue) {
      const value = String(levelValue || '').trim()
      if (value === '4') return 'level-critical'
      if (value === '3') return 'level-high'
      if (value === '2') return 'level-mid'
      return 'level-low'
    },
    formatHour(value) {
      if (!value) return '—'
      try {
        return new Date(value).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
      } catch (error) {
        return '—'
      }
    },
    goBack() {
      this.$router.push({ name: 'RHModule' })
    },
  },
}
</script>

<style scoped>
.capture-root {
  min-height: 100vh;
  background:
    radial-gradient(circle at 5% 0%, rgba(248, 113, 113, 0.16), transparent 35%),
    radial-gradient(circle at 92% 2%, rgba(220, 38, 38, 0.12), transparent 32%),
    #eef3fb;
  padding: 76px 16px 24px;
}
.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: #ffc434;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  z-index: 20;
}
.logo-button { height: 65%; object-fit: contain; cursor: pointer; }
.topbar-actions { display: flex; align-items: center; gap: 8px; }
.config-btn {
  border: 1px solid #0f172a;
  background: #fff;
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
}
.refresh-btn {
  border: none;
  background: #111827;
  color: #fff;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
}
.header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(148, 163, 184, 0.26);
  border-radius: 14px;
  padding: 8px 12px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.07);
}
.header h1 { margin: 0; color: #0f172a; font-size: 1.1rem; line-height: 1.1; }
.header-actions {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}
.kpi-card {
  border: 1px solid #fecaca;
  background: #ffffff;
  border-radius: 12px;
  padding: 10px 12px;
  display: grid;
  gap: 2px;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.06);
}
.kpi-card--primary {
  border-color: #fca5a5;
  background: linear-gradient(180deg, #ffffff, #fef2f2);
}
.kpi-card--accent {
  border-color: #fcd34d;
  background: linear-gradient(180deg, #ffffff, #fffbeb);
}
.kpi-label {
  font-size: 0.74rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
}
.kpi-value {
  font-size: 1.12rem;
  color: #0f172a;
}
.month-picker,
.sort-picker {
  display: grid;
  gap: 4px;
  font-size: 0.82rem;
  font-weight: 600;
}
.month-picker input,
.sort-picker select {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px;
}
.sort-dir-btn {
  border: 1px solid #94a3b8;
  background: #fff;
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 700;
}
.department-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 8px 0 10px;
}
.department-tab-btn {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 999px;
  padding: 6px 11px;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.82rem;
}
.department-tab-btn--active {
  border-color: #b91c1c;
  background: #b91c1c;
  color: #ffffff;
}
.table-wrap {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #fde0e0;
  border-radius: 12px;
  overflow: auto;
  box-shadow: 0 20px 36px rgba(15, 23, 42, 0.09);
}
.matrix-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.matrix-table th,
.matrix-table td {
  border-bottom: 1px solid #edf2fb;
  padding: 4px;
  text-align: center;
}
.matrix-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  color: #0f172a;
  border-bottom: 1px solid #fde0e0;
  border-right: 1px solid #edf2fb;
  font-weight: 700;
  font-size: 0.8rem;
}
.matrix-table thead th.day-header {
  background: #f8fafc;
  color: #334155;
}
.matrix-table thead th.week-start-header {
  background: #fef2f2;
  color: #991b1b;
  box-shadow: inset 0 -2px 0 #fca5a5;
}
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 3;
  background: #fffafa;
  text-align: left !important;
}
.sticky-name {
  min-width: 220px;
  font-weight: 700;
}
.name-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.name-text {
  color: #0f172a;
}
.day-cell {
  border: 0;
  background: #f8fafc;
  border-radius: 7px;
  min-width: 24px;
  padding: 5px 4px;
  color: #334155;
  font-size: 0.76rem;
  transition: transform 0.14s ease, box-shadow 0.14s ease, background 0.14s ease;
}
.day-cell.is-clickable {
  cursor: pointer;
  font-weight: 700;
}
.day-cell.is-clickable:hover {
  transform: translateY(-1px);
}
.day-cell.is-empty {
  color: #94a3b8;
  background: #f8fafc;
}
.week-start-col {
  background: rgba(254, 242, 242, 0.65);
}
.total-cell {
  font-weight: 700;
  background: rgba(248, 113, 113, 0.08);
}
.total-pill {
  display: inline-flex;
  min-width: 36px;
  justify-content: center;
  border-radius: 999px;
  padding: 3px 8px;
  border: 1px solid transparent;
}
.total-pill.strong {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fca5a5;
}
.total-pill.medium {
  background: #ffedd5;
  color: #9a3412;
  border-color: #fdba74;
}
.total-pill.soft {
  background: #fef3c7;
  color: #92400e;
  border-color: #fcd34d;
}
.total-pill.neutral {
  background: #e2e8f0;
  color: #334155;
  border-color: #cbd5e1;
}
.empty-box, .error-box {
  margin-top: 12px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 12px;
}
.error-box { color: #b91c1c; border-color: #fecaca; }
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  backdrop-filter: blur(2px);
}
.modal-card {
  width: min(820px, 100%);
  max-height: 82vh;
  background: linear-gradient(180deg, #ffffff, #fff7f7);
  border-radius: 14px;
  border: 1px solid #f3d2d2;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 46px rgba(2, 6, 23, 0.25);
}
.modal-head {
  padding: 12px;
  border-bottom: 1px solid #f3d6d6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  background: linear-gradient(90deg, #fef2f2, #fff7ed);
}
.modal-head h3 {
  margin: 0;
  color: #0f172a;
}
.modal-subtitle {
  margin: 2px 0 0;
  font-size: 0.82rem;
  color: #475569;
}
.modal-actions {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #fffafa;
}
.options-list {
  overflow: auto;
  padding: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px;
}
.option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 10px;
  border: 1px solid #f0dada;
  border-radius: 10px;
  background: #fff;
}
.small-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 600;
}
.notes-modal { width: min(1040px, 100%); }
.notes-table { width: 100%; border-collapse: collapse; font-size: 0.86rem; }
.notes-table th, .notes-table td {
  border-bottom: 1px solid #f1e2e2;
  padding: 8px;
  text-align: left;
  vertical-align: top;
}
.notes-table thead th {
  background: #fef2f2;
  color: #7f1d1d;
  font-weight: 700;
  position: sticky;
  top: 0;
}
.notes-table tbody tr:nth-child(even) td {
  background: #fffbfb;
}
.notes-table tbody tr:hover td {
  background: #fff5f5;
}
.desc-cell {
  max-width: 320px;
  white-space: pre-wrap;
}
.level-pill {
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 0.73rem;
  font-weight: 700;
  border: 1px solid transparent;
  white-space: nowrap;
}
.level-pill.level-low {
  background: #fef9c3;
  color: #854d0e;
  border-color: #fde68a;
}
.level-pill.level-mid {
  background: #ffedd5;
  color: #9a3412;
  border-color: #fdba74;
}
.level-pill.level-high {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fca5a5;
}
.level-pill.level-critical {
  background: #7f1d1d;
  color: #fff5f5;
  border-color: #7f1d1d;
}
.signed-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 0.73rem;
  font-weight: 800;
  border: 1px solid transparent;
}
.signed-pill--yes {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}
.signed-pill--no {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fecaca;
}
.empty-cell { text-align: center !important; color: #64748b; }
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
@media (max-width: 980px) {
  .kpi-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .header {
    flex-direction: column;
    align-items: flex-start;
  }
}
@media (max-width: 640px) {
  .kpi-strip {
    grid-template-columns: 1fr;
  }
}
</style>
